// File: src/utils/api.js
import React from 'react'
import { AuthContext } from '../context/AuthContext'

// Use environment variable for API base URL
const BASE_URL = process.env.REACT_APP_API_URL || 'https://intranet.nesimltd.co.ke';

// Helper to build the full URL
function buildUrl(path) {
  // If path is already a full URL, return it as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    console.log('🔗 Path is already a full URL:', path);
    return path;
  }
  
  const p = path.startsWith('/') ? path : `/${path}`
  const fullUrl = `${BASE_URL}${p}`
  console.log('🔗 Building URL:', { path, BASE_URL, fullUrl })
  return fullUrl
}

/**
 * Thin fetch‑wrapper that auto‑attaches `Authorization: Bearer …` and base URL.
 */
export async function authFetch(url, token, options = {}) {
  const fullUrl = buildUrl(url)
  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  }
  
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    let res = await fetch(fullUrl, { 
      ...options, 
      headers, 
      credentials: 'include',
      signal: controller.signal
    })
    
    clearTimeout(timeoutId);

    // Retry once on 401
    if (res.status === 401) {
      const retryToken = localStorage.getItem('token')
      const retryController = new AbortController();
      const retryTimeoutId = setTimeout(() => retryController.abort(), 30000);
      
      try {
        const retryHeaders = {
          ...(options.headers || {}),
          Authorization: `Bearer ${retryToken}`,
        }
        res = await fetch(fullUrl, { 
          ...options, 
          headers: retryHeaders, 
          credentials: 'include',
          signal: retryController.signal
        })
        clearTimeout(retryTimeoutId);
      } catch (retryErr) {
        clearTimeout(retryTimeoutId);
        if (retryErr.name === 'AbortError') {
          throw new Error('Request timeout after 30 seconds');
        }
        throw retryErr;
      }
    }
    return res;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Request timeout after 30 seconds');
    }
    throw err;
  }
}

// Shorthand helpers
export const apiGet = (url, token) =>
  authFetch(url, token, { method: 'GET' })

export const apiPost = (url, token, body) =>
  authFetch(url, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

export const apiPut = (url, token, body) =>
  authFetch(url, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

export const apiDelete = (url, token) =>
  authFetch(url, token, { method: 'DELETE' })

/**
 * Submit support/bug report form
 * @param {Object} data - The form data
 * @returns {Promise<Response>}
 */
export async function submitSupportForm(data) {
  const url = buildUrl('/api/support');
  let res;
  if (data.attachment) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'attachment' && value) {
        formData.append('attachment', value);
      } else if (Array.isArray(value)) {
        value.forEach(v => formData.append(key, v));
      } else {
        formData.append(key, value);
      }
    });
    res = await fetch(url, {
      method: 'POST',
      body: formData,
    });
  } else {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(errText || 'Failed to submit support ticket');
  }
  try {
    return await res.json();
  } catch {
    return { success: true, ticketId: undefined };
  }
}

/**
 * Get a single bug report by ID
 * @param {string} id - The bug ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export async function getBug(id, token) {
  const res = await authFetch(`/api/support/bugs/${id}`, token);
  if (!res.ok) {
    throw new Error('Failed to fetch bug report');
  }
  return res.json();
}

/**
 * Get user's own bug reports
 * @param {string} token - Auth token
 * @returns {Promise<Array>}
 */
export async function getMyBugs(token) {
  const res = await authFetch('/api/support/my-bugs', token);
  console.log('📡 Response status:', res.status);
  if (!res.ok) {
    const errorText = await res.text();
    console.error('❌ API Error:', errorText);
    throw new Error('Failed to fetch your bug reports');
  }
  const data = await res.json();
  console.log('✅ API Response data:', data);
  return data;
}

/**
 * Get comments for a bug
 * @param {string} bugId - The bug ID
 * @param {string} token - Auth token
 * @returns {Promise<Array>}
 */
export async function getBugComments(bugId, token) {
  const res = await authFetch(`/api/support/bugs/${bugId}/comments`, token);
  if (!res.ok) {
    throw new Error('Failed to fetch comments');
  }
  return res.json();
}

/**
 * Add a comment to a bug
 * @param {string} bugId - The bug ID
 * @param {Object} data - Comment data
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export async function addBugComment(bugId, data, token) {
  const url = buildUrl(`/api/support/bugs/${bugId}/comments`);
  
  // If data is already FormData, use it directly
  const formData = data instanceof FormData ? data : new FormData();
  
  // If data is not FormData, process it
  if (!(data instanceof FormData)) {
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'attachments' && Array.isArray(value)) {
        value.forEach(file => formData.append('attachments', file));
      } else {
        formData.append(key, value);
      }
    });
  }
  
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
    body: formData,
  });
  
  if (!res.ok) {
    throw new Error('Failed to add comment');
  }
  return res.json();
}

/**
 * Update a comment
 * @param {string} bugId - The bug ID
 * @param {string} commentId - The comment ID
 * @param {Object} data - Updated comment data
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export async function updateBugComment(bugId, commentId, data, token) {
  const res = await authFetch(`/api/support/bugs/${bugId}/comments/${commentId}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  
  if (!res.ok) {
    throw new Error('Failed to update comment');
  }
  return res.json();
}

/**
 * Delete a comment
 * @param {string} bugId - The bug ID
 * @param {string} commentId - The comment ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export async function deleteBugComment(bugId, commentId, token) {
  const res = await authFetch(`/api/support/bugs/${bugId}/comments/${commentId}`, token, { method: 'DELETE' });
  
  if (!res.ok) {
    throw new Error('Failed to delete comment');
  }
  return res.json();
}

/**
 * Hook version with built‑in 401‑retry + logout
 */
export function useAuthFetch() {
  const { token, logout, refreshUser } = React.useContext(AuthContext)
  return React.useCallback(
    async (url, options = {}) => {
      const fullUrl = buildUrl(url)
      const headers = {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      }
      let res = await fetch(fullUrl, { ...options, headers })

      if (res.status === 401) {
        await refreshUser()
        const retryToken = localStorage.getItem('token')
        const retryHeaders = {
          ...(options.headers || {}),
          Authorization: `Bearer ${retryToken}`,
        }
        res = await fetch(fullUrl, { ...options, headers: retryHeaders })
        if (res.status === 401) logout()
      }
      return res
    },
    [token, logout, refreshUser]
  )
}

/**
 * Simple “useApi” wrapper
 */
export function useApi() {
  const fetcher = useAuthFetch()
  return {
    get: url =>
      fetcher(url, { method: 'GET' }),
    post: (url, body) =>
      fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    put: (url, body) =>
      fetcher(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    delete: url =>
      fetcher(url, { method: 'DELETE' }),
  }
}
